<template>
  <multiform-controller
    v-show="petsValidationComplete"
    :product-name="productName"
    :step-id="stepId"
    :questions="questions"
    :forms-data="pets"
    :initial-data="PET"
    form-label="pet"
    :product-title="productTitle"
    :max-amount="5"
    :is-continue-triggered="isContinueTriggered"
    :items-validity="petsValidity"
    @edit-process="$emit('edit-process', $event)"
    @multiform-questions-details="onErrorDetailsUpdate"
    @multiform-changed="handleMultiformChangesStatus"
    @show-form-actions="$emit('show-form-actions', isAllPetsValid)"
    @set-data="handleSetPets"
    @input="currentForm = $event"
    @status="handleMultiformStatus"
    @reset-continue-trigger="$emit('reset-continue-trigger', $event)"
  >
    <template #info>
      <form-row
        class="mb-4"
        label="Would you like to add another pet?"
      >
        <template #helpText>
          <div class="d-flex align-start">
            <v-icon
              class="mr-4"
              color="primary"
            >
              mdi-credit-card-outline
            </v-icon>
            <p><strong>Price sensitive question</strong><br> Some insurers may offer a discount if you add another pet to your policy. However, not all insurers will offer this type of policy.</p>
          </div>
        </template>
      </form-row>
    </template>

    <template
      #preview="{ data: { data }, index: { index } }"
    >
      <subform-preview
        :preview-list="formatPreviewItem(data, index)"
        :icon="constants.PET_ICONS[data.pet]"
      />
    </template>
  </multiform-controller>
</template>

<script>
import { capitalize } from 'lodash/string';
import QuestionBase from './Base';
import SubformPreview from '@/components/SubformController/SubformPreview';
import MultiformController from '@/components/MultiformController/MultiformController';
import { initCoverRules } from '@/prevalidation/pet';
import { PET } from '@/payloadTemplates/pet';
import { validate } from 'vee-validate';

export default {
  name: 'question-pets',
  extends: QuestionBase,
  components: {
    MultiformController,
    SubformPreview,
  },
  inject: [
    'constants',
    'helpers',
  ],
  props: {
    productTitle: {
      type: String,
      required: true,
    },
    isContinueTriggered: {
      type: Boolean,
      required: true,
    },
    isPetMinimiseHelptextExperiment: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      currentForm: {},
      pets: [],
      multiFormStatus: null,
      petsValidity: [],
      errorsQuestionDetails: null,
    };
  },
  watch: {
    'currentForm.petHealth' (val) {
      // We get the petHealth as undefined when cancelling on an add new pet form action
      this.$emit('show-form-actions', val === false || val === undefined);
    },
    petsValidity () {
      // Also check if the currentForm.petHealth is not null so that the button is not shown before selecting a health situation(PU-4362)
      this.$emit('show-form-actions', this.isAllPetsValid && this.currentForm.petHealth !== null);
    },
    // need for adding dynamicTitle to errors section
    // TODO:refactor - get rid of corresponding code on vue 3 update
    questions (questions) {
      if (!this.errorsQuestionDetails) return;
      const remappedDetails = this.errorsQuestionDetails.map(questionDetails => {
        const updatedQuestion = questions.find(({ questionId }) => questionId === questionDetails.questionId);
        return {
          ...questionDetails,
          title: updatedQuestion.params.title,
        };
      });
      this.$emit('multiform-questions-details', remappedDetails);
    },
  },
  computed: {
    petsValidationComplete () {
      if (!this.pets.length) return true;
      return !!this.petsValidity.length;
    },
    isEditingFirstPet () {
      return this.pets.length === 1 &&
        this?.multiFormStatus?.isEditProcess &&
        !this?.multiFormStatus?.showNewForm;
    },
    petName () {
      return this.currentForm?.petName || 'pet';
    },
    isAllPetsValid () {
      return !this.petsValidity.some(isPetValid => isPetValid === false);
    },
    possessiveName () {
      return this.currentForm?.petName ? `${this.petName}'s` : this.petName;
    },
    petOptions () {
      const defaultOptions = [
        {
          value: 'dog',
          text: 'Dog',
          icon: this.constants.PET_ICONS.dog,
        },
        {
          value: 'cat',
          text: 'Cat',
          icon: this.constants.PET_ICONS.cat,
        },
        {
          value: 'rabbit',
          text: 'Rabbit',
          icon: this.constants.PET_ICONS.rabbit,
        },
      ];

      if (!this.pets.length) {
        return defaultOptions;
      }
      // show all pet options when we try to edit the only existing pet
      if (this.isEditingFirstPet) {
        return defaultOptions;
      }

      const pet = this.pets[0].pet;

      if (pet === 'dog' || pet === 'cat') {
        return defaultOptions.filter(option => option.value !== 'rabbit');
      }

      return [defaultOptions.find(option => option.value === 'rabbit')];
    },
    petHelpText () {
      // Show help text tip to add new pets when adding or editing first pet
      if (!this.pets?.length || this.isEditingFirstPet) {
        return '<strong>Tip:</strong> Insuring more than one pet? Start by entering the details of your first pet. You\'ll have the option to add more pets later.';
      };

      switch (this.pets[0].pet) {
        case 'dog':
        case 'cat':
          return '<p>Please indicate which type of animal your pet is.</p> <p>Please note, only additional dogs or cats can be added to this quote. If you wish to insure your rabbit, please complete a separate, new quote.</p>';
        case 'rabbit':
          return '<p>Please indicate which type of animal your pet is.</p> <p>Please note, only additional rabbits can be added to this quote. If you wish to insure your cats or dogs, please complete a separate, new quote.</p>';
        default:
          return null;
      }
    },
    isQuotezonePartner () {
      return this.helpers.isQuotezonePartner(this.config.partner);
    },
    isCNIPartner () {
      return [this.constants.CNI_PARTNER, this.constants.CNI_PARTNER_ALTERNATIVE].includes(this.config.partner);
    },
    existingMedicalConditionsLink () {
      if (this.isQuotezonePartner) return 'https://www.quotezone.co.uk/pet-insurance/existing-conditions';
      if (this.isCNIPartner) return 'https://www.compareni.com/insurance/pre-existing-medical-conditions-pet';
      return '';
    },
    petHealthHelpText () {
      if (this.currentForm.petHealth) {
        const commonHelpText = `Most insurance providers won't cover your pet for any existing medical conditions, illnesses,
            or injuries that your pet has attained before taking out a policy.`;

        if (this.isQuotezonePartner || this.isCNIPartner) {
          return `${commonHelpText} <br> A specialist provider is required to cover known conditions,
            <a href="${this.existingMedicalConditionsLink}" target="_blank" >please click here to see our list of specialist providers.</a>`;
        }

        return `${commonHelpText} Whilst some of our providers offer this, a specialist provider is required to cover known conditions.
          If you're not sure what falls under pre-existing, we recommend you speak directly to your chosen insurer.`;
      }

      return null;
    },
    questions () {
      return [
        {
          questionId: 'pet',
          questionComponent: 'question-base-radio-list',
          params: {
            customFields: {
              field: 'pet',
            },
            title: 'What type of animal is your pet?',
            helpText: this.petHelpText,
            optionsConfig: {
              options: this.petOptions,
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select the type of your pet.',
              },
            },
          },
        },
        {
          questionId: 'pet-name',
          questionComponent: 'question-base-text',
          params: {
            customFields: {
              field: 'petName',
            },
            title: 'What is your pet\'s name? ',
            maxLength: 50,
            placeholder: 'e.g. Lola or Rex',
            prevalidationRules: {
              field: {
                required: true,
                name: [2, 50],
                regex: '^([A-Za-z-\\s]|[À-Ö]|[Ø-ö]|[ø-ǿ]|[Ȁ-ʯ]|[̀-ͯ]|[Ḁ-ỿ]|[Ⅰ-ↈ])+$',
              },
            },
            validationRules: {
              field: {
                required: true,
                name: [2, 50],
                regex: '^([A-Za-z-\\s]|[À-Ö]|[Ø-ö]|[ø-ǿ]|[Ȁ-ʯ]|[̀-ͯ]|[Ḁ-ỿ]|[Ⅰ-ↈ])+$',
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter your pet\'s name.',
              },
            },
          },
        },
        {
          questionId: 'pet-gender',
          questionComponent: 'question-base-radio-list',
          params: {
            customFields: {
              field: 'petGender',
            },
            title: `Is ${this.petName}  male or female?`,
            optionsConfig: {
              options: {
                M: 'Male',
                F: 'Female',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select your pet\'s gender.',
              },
            },
          },
        },
        {
          questionId: 'pet-date-of-birth',
          questionComponent: 'question-base-date',
          params: {
            customFields: {
              field: 'petDateOfBirth',
            },
            maxYears: 20,
            title: `What is ${this.possessiveName} date of birth?`,
            helpText: {
              value: 'The premium offered by insurers will depend on the age of your pet, so we need to know how old they are in order to provide you with the most accurate quote. If you are unsure of your pet\'s exact date of birth, just provide the best estimate.',
              collapsible: this.isPetMinimiseHelptextExperiment,
              openerText: 'Pet Age for Accurate Quotes',
            },
            prevalidationRules: {
              field: {
                dateOfBirth: true,
              },
            },
            validationRules: {
              field: {
                dateOfBirth: true,
                petCheckMinAge: true,
              },
            },
          },
        },
        {
          questionId: 'pet-health',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'petHealth',
            },
            title: `Do you need to cover any pre-existing medical conditions for ${this.petName}?`,
            helpText: this.petHealthHelpText,
            prevalidationRules: {
              is: {
                other: false,
              },
              field: {
                required: false,
              },
            },
            validationRules: {
              field: {
                is: {
                  other: false,
                },
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select your pet\'s health status.',
              },
            },
          },
        },
        {
          questionId: 'pet-breed-type',
          questionComponent: 'question-base-dropdown',
          params: {
            customFields: {
              field: 'petBreedType',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            showRules: {
              pet: {
                isAny: {
                  other: [
                    'dog',
                  ],
                },
                required: true,
              },
            },
            title: `What type of dog is ${this.petName}?`,
            helpText: {
              value: 'If your dog\'s parents were both of the same breed then please select \'Pedigree\'. If your dog is a cross between two breeds, and you know the names of these breeds, please select \'Cross-breed\'. If your dog is a cross between more than two breeds, or you are unsure of your dog\'s breed, then please select \'Mixed\'.',
              collapsible: this.isPetMinimiseHelptextExperiment,
              openerText: 'Dog Breed Classification Options',
            },
            optionsConfig: {
              options: {
                Mixed: 'Mixed',
                Pedigree: 'Pedigree',
                'Cross-breed': 'Cross-breed',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm your dog\'s breed type.',
              },
            },
          },
        },
        {
          questionId: 'pet-breed',
          questionComponent: 'question-base-search-other',
          params: {
            customFields: {
              search: 'petBreed',
            },
            renderAfter: {
              pet: {
                is_not: {
                  other: null,
                },
                required: true,
              },
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
              petBreedType: {
                is_not: 'Mixed',
              },
            },
            title: `What is ${this.possessiveName} primary breed?`,
            searchType: 'getPetBreeds',
            optionsConfig: {
              placeholder: 'Please type to select the breed',
              changedPlaceholder: 'Please enter the breed',
            },
            responseModel: {
              value: 'value',
              name: 'breedName',
            },
            payload: {
              petType: '@pet',
              breedType: '@petBreedType',
            },
            prevalidationRules: {
              search: {
                required: true,
              },
            },
            validationRules: {
              search: {
                required: true,
              },
            },
            validationErrors: {
              search: {
                required: 'Please select the correct breed type.',
              },
            },
          },
        },
        {
          questionId: 'cross-breed-size',
          questionComponent: 'question-base-dropdown',
          params: {
            customFields: {
              field: 'crossBreedSize',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            showRules: {
              petBreedType: {
                isAny: {
                  other: [
                    'Cross-breed',
                    'Mixed',
                  ],
                },
                required: true,
              },
            },
            title: `What size of dog is ${this.petName}?`,
            helpText: 'Please select the most accurate size for your dog\'s fully-grown weight from the options below.',
            optionsConfig: {
              options: {
                'Small (up to 10kg)': 'Small (up to 10kg)',
                'Medium (10 - 20kg)': 'Medium (10 - 20kg)',
                'Large (more than 20kg)': 'Large (more than 20kg)',
              },
            },
            defaultValues: {
              field: null,
            },
            resetAfterChanges: ['petBreedType'],
          },
        },
        {
          questionId: 'pet-purchase-price',
          questionComponent: 'question-base-number',
          params: {
            customFields: {
              field: 'petPurchasePrice',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            title: `How much did you pay for ${this.petName}?`,
            helpText: 'Please enter how much you paid (or donated) for your pet, to the nearest pound. If you didn\'t pay anything for your pet, please enter 0.',
            inputPrefix: '£',
            maxLength: 8,
            placeholder: 'e.g. 200',
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter a value e.g. 200',
              },
            },
          },
        },
        {
          questionId: 'chipped-tagged',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'chippedTagged',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            title: `Has ${this.petName} been chipped/tagged?`,
            helpText: {
              value: 'A microchip which contains a unique identification number is implanted in the animal. This is commonly used to identify animals if they are lost or stolen. If you are unsure whether your pet has been chipped or tagged then please select "No".',
              collapsible: this.isPetMinimiseHelptextExperiment,
              openerText: 'Microchipping and Pet Identification',
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select Yes if your pet is tagged/chipped.',
              },
            },
          },
        },
        {
          questionId: 'pet-vaccination-up-to-date',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'petVaccinationUpToDate',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            title: `Are ${this.petName}'s vaccinations up to date?`,
            helpText: {
              value: 'If your pet suffers from an illness that could have been prevented by a routine vaccination, then the insurer may refuse to pay the claim. Although pets aren\'t required to have their vaccinations up to date in order to be insured, it\'s best to carefully read the insurer\'s terms and conditions.',
              collapsible: this.isPetMinimiseHelptextExperiment,
              openerText: 'Vaccinations and Insurance Claims',
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select Yes if your pet\'s vaccinations is up to date.',
              },
            },
          },
        },
        {
          questionId: 'neutered',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'neutered',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            title: `Has ${this.petName} been neutered or spayed?`,
            helpText: 'Neutering is when your pet has had an operation to make it sterile. Please indicate whether your pet has had this procedure or not.',
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select Yes if your pet is neutered.',
              },
            },
          },
        },
        {
          questionId: 'professional-use',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'professionalUse',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            showRules: {
              pet: {
                isAny: {
                  other: ['dog'],
                },
                required: true,
              },
            },
            title: `Is ${this.petName} used in a professional capacity as any of the following: guarding, security services or racing?`,
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionId: 'aggressivity',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'aggressivity',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            showRules: {
              pet: {
                isAny: {
                  other: ['dog'],
                },
                required: true,
              },
            },
            title: `Has ${this.petName} ever displayed any vicious/aggressive tendencies?`,
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionId: 'aggressiv-attack',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'aggressivAttack',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            showRules: {
              pet: {
                isAny: {
                  other: ['dog'],
                },
                required: true,
              },
            },
            title: `Has ${this.petName} bitten somebody or caused a road traffic accident for which you could be held responsible?`,
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionId: 'pet-place-alcohol-served',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'petPlaceAlcoholServed',
            },
            renderAfter: {
              petHealth: {
                isAny: {
                  other: [false],
                },
                required: true,
              },
            },
            title: `Does ${this.petName} live on a premises where alcohol is served?`,
            helpText: 'This includes a pub or other licensed premises.',
            defaultValues: {
              field: false,
            },
          },
        },
      ];
    },
  },
  async created () {
    this.pets = this.stepData.pets;
    this.petsValidity = await this.checkPetsValidity();
    this.$emit('show-form-actions', !this.currentForm.petHealth);
  },
  methods: {
    async handleMultiformChangesStatus ($event) {
      this.petsValidity = await this.checkPetsValidity();
      this.$emit('multiform-changed', $event);
    },
    async checkPetsValidity () {
      const petFields = this.questions
        .map(({ params }) => params.customFields.field || params.customFields.search)
        .sort();
      const petRules = initCoverRules()(this.stepData);
      const promises = await Promise.all((this.stepData.pets || []).map(async (pet, petIndex) => await Promise.all(
        petFields.map(field => validate(pet[field], petRules[petIndex][field])),
      )));
      return promises.map(item => item.every(rule => rule.valid));
    },
    handleSetPets (e) {
      this.pets = e;
      this.$emit('input', { ...this.stepData, pets: e });
    },
    handleMultiformStatus ($event) {
      this.multiFormStatus = $event;
    },
    formatPreviewItem (
      { petName, pet, petBreedType, petBreed, petDateOfBirth },
      index,
    ) {
      const items = [
        {
          prepend: 'Name:',
          value: petName,
        },
        {
          prepend: 'Type of Animal:',
          value: capitalize(pet),
        },
        {
          prepend: 'Date of Birth:',
          value: petDateOfBirth,
        },
      ];

      if (petBreed) {
        items.splice(
          2,
          0,
          {
            prepend: 'Breed:',
            value: petBreed,
          },
        );
      }

      if (pet === 'dog') {
        items.splice(
          2,
          0,
          {
            prepend: 'Type of Dog:',
            value: petBreedType,
          },
        );
      }

      if (!this.petsValidity[index]) {
        items.push({
          prepend: '<span class="error--text">There are some questions that you haven\'t answered </span>',
        });
      }

      return items;
    },
    onErrorDetailsUpdate (data) {
      this.errorsQuestionDetails = data;
      this.$emit('multiform-questions-details', data);
    },
  },
  setup () {
    return {
      PET,
    };
  },
};
</script>

<style lang="scss">
// [PU-3791] - change 'petHealth' question background color to red when select 'Yes' even when validate() wasn't called
#pet-health {
  .form-section__validity .v-icon {
    transition-duration: 0s;
  }

  &.is-invalid[changed="true"] {
    .form-section__container::before {
      display: block;
      background-color: var(--v-error-base, $clr-error);
    }

    .help-text__bg {
      fill: var(--v-error-base, $clr-error);
    }

    // [PU-3791] - change icon from 'check' to 'cross' in order to fake validation behavior for 'petHealth' question
    .form-section__validity .v-icon {
      font-size: 30px !important;
      color: var(--v-error-base, $clr-error) !important;
      caret-color: var(--v-error-base, $clr-error) !important;

      &::before {
        content: "\F0159" !important;
      }
    }
  }
}
</style>
